// DEV-329: one definition of "headcount" for the client page. The header,
// HR, Compliance and Open Enrollment each counted employees differently
// (active only vs. everyone vs. non-terminated), so the same client showed
// 4 in the header and 5 in the tabs. Headcount = active + pending activation
// (hired, login not yet activated); every surface shows both numbers.
import { normalizeEmployeeStatus } from "@shared/utils/normalizeEmployeeStatus";

type StatusLike = { status?: string | null };
type EmploymentLike = { employmentType?: string | null; scheduledHours?: string | null };

export interface EmployeeHeadcount {
  active: number;
  pendingActivation: number;
  /** active + pendingActivation */
  total: number;
  /** Employees in any other status (terminated, COBRA, leave, ...) — not headcount. */
  other: number;
}

function headcountStatus(emp: StatusLike): "active" | "pending_activation" | null {
  // Fallback "inactive" so a blank/unknown status is never counted.
  const s = normalizeEmployeeStatus(emp.status, "inactive");
  return s === "active" || s === "pending_activation" ? s : null;
}

export function isCountedInHeadcount(emp: StatusLike): boolean {
  return headcountStatus(emp) !== null;
}

export function getEmployeeHeadcount(employees: readonly StatusLike[]): EmployeeHeadcount {
  let active = 0;
  let pendingActivation = 0;
  for (const emp of employees) {
    const s = headcountStatus(emp);
    if (s === "active") active++;
    else if (s === "pending_activation") pendingActivation++;
  }
  return {
    active,
    pendingActivation,
    total: active + pendingActivation,
    other: employees.length - active - pendingActivation,
  };
}

/** "4 active · 1 pending activation" (pending part omitted when zero). */
export function formatHeadcountBreakdown(h: EmployeeHeadcount): string {
  const parts = [`${h.active} active`];
  if (h.pendingActivation > 0) parts.push(`${h.pendingActivation} pending activation`);
  return parts.join(" · ");
}

export interface DistributionBucket {
  label: string;
  count: number;
  /** Share of `population`, 0–100. */
  pct: number;
}

/**
 * DEV-359: bucket a population and compute each bucket's share of that same
 * population, largest first. Pass the headcount population so buckets sum to
 * the headcount and no share exceeds 100% (Performance Distribution used to
 * count terminated employees against the active headcount: "10 (111%)").
 */
export function buildDistribution<T>(population: readonly T[], keyOf: (emp: T) => string): DistributionBucket[] {
  const counts = new Map<string, number>();
  for (const emp of population) {
    const key = keyOf(emp);
    counts.set(key, (counts.get(key) || 0) + 1);
  }
  const total = population.length;
  return Array.from(counts, ([label, count]) => ({ label, count, pct: total > 0 ? (count / total) * 100 : 0 }))
    .sort((a, b) => b.count - a.count);
}

export type EmploymentClass = "full_time" | "part_time" | "not_set";

/**
 * Full-time / part-time for compliance math. A blank employment type is
 * "not_set" — it used to fall through to part-time (with 0 hours) and skew
 * FTE. Other non-blank types (variable hour, seasonal, ...) keep the old
 * scheduled-hours rule.
 */
export function classifyEmploymentType(emp: EmploymentLike): EmploymentClass {
  const type = (emp.employmentType ?? "").trim().toLowerCase();
  if (!type) return "not_set";
  if (type.includes("full")) return "full_time";
  if (type.includes("part")) return "part_time";
  const hours = parseFloat(emp.scheduledHours || "0");
  return !isNaN(hours) && hours >= 40 ? "full_time" : "part_time";
}

export function scheduledWeeklyHours(emp: EmploymentLike): number {
  const n = parseFloat(emp.scheduledHours || "0");
  return isNaN(n) ? 0 : n;
}

export interface ComplianceEmployeeStats<T> {
  /** Headcount population (active + pending activation), input order kept. */
  employees: T[];
  headcount: EmployeeHeadcount;
  fullTime: T[];
  partTime: T[];
  /** Blank employment type — shown separately, left out of FTE. */
  notSet: T[];
  partTimeHoursPerMonth: number;
  /** Part-time monthly hours ÷ 130 (unrounded). */
  partTimeFte: number;
  /** fullTime + partTimeFte (unrounded). */
  totalFte: number;
}

/**
 * DEV-362: one set of compliance counting rules for the broker Compliance tab
 * and the employer Compliance page (the employer page counted terminated
 * employees and treated a blank employment type as part-time).
 */
export function getComplianceEmployeeStats<T extends StatusLike & EmploymentLike>(
  employees: readonly T[],
): ComplianceEmployeeStats<T> {
  const counted = employees.filter(isCountedInHeadcount);
  const fullTime = counted.filter((e) => classifyEmploymentType(e) === "full_time");
  const partTime = counted.filter((e) => classifyEmploymentType(e) === "part_time");
  const notSet = counted.filter((e) => classifyEmploymentType(e) === "not_set");
  const partTimeHoursPerMonth = partTime.reduce((acc, e) => acc + scheduledWeeklyHours(e) * 4.33, 0);
  const partTimeFte = partTimeHoursPerMonth / 130;
  return {
    employees: counted,
    headcount: getEmployeeHeadcount(counted),
    fullTime,
    partTime,
    notSet,
    partTimeHoursPerMonth,
    partTimeFte,
    totalFte: fullTime.length + partTimeFte,
  };
}
